import { FeedEvent } from '@urbanmix-tech/shared-js';

import { convertPseudoDateToRealDate } from '@/helpers/common';
import {
  StatusEvent,
  NativeLeaseEvent,
  isRecognizedEvent,
  type StatusEventData,
} from './events';

/**
 * PORTED from `platform-workflows/src/core/unit-status/derive.ts`. The fold is
 * unchanged — keep it that way so both sides agree; replace this file wholesale
 * when upstream changes rather than hand-patching it.
 *
 * The single local addition is in `toMillis`: upstream handles Date / ISO string
 * / epoch / Firestore timestamp shapes, but this app also passes dates around as
 * `"__Timestamp__<iso>"` strings, which upstream would parse to NaN and collapse
 * to epoch 0 — silently destroying event ordering. So an unparseable value gets
 * one more attempt through the app's own date normalizer.
 */

export interface CurrentTenantStatus {
  tenantName?: string;
  /** Renewal in progress for the current tenant, before it's executed. */
  renewal?: 'negotiating' | 'terms_agreed';
  /** Current tenant renewed (lease executed — stays in place). */
  renewed?: boolean;
  /** Move-out scheduled for the current tenant. */
  moveOut?: { date?: string };
}

/** How far the incoming tenant's lease has progressed. */
export type LeaseStage = 'created' | 'sent' | 'executed';

export interface IncomingTenantStatus {
  tenantName?: string;
  /** Lease pipeline stage (drafted → sent → executed). */
  lease?: LeaseStage;
  moveIn?: { date?: string };
}

export interface UnitStatusView {
  occupancy: 'occupied' | 'vacant';
  /** The sitting tenant (null once moved out). */
  current: CurrentTenantStatus | null;
  /** The next tenant, if a re-lease is in progress. */
  incoming: IncomingTenantStatus | null;
  listing: 'listed' | 'showing' | null;
  /** Human-readable one-line summary. */
  label: string;
  /** ISO timestamp of the most recent event considered (null if none). */
  asOf: string | null;
}

export interface DeriveOptions {
  /**
   * Maps a feed event's `processId` to its process `type` (e.g. "new_lease",
   * "lease_renewal"). Used to classify native lease events (which don't carry a
   * lease type) as an incoming new lease vs a current-tenant renewal. When a
   * process id is absent from the map, native lease events default to a new
   * (incoming) lease.
   */
  processTypeById?: Record<string, string>;
}

const LEASE_STAGE_RANK: Record<LeaseStage, number> = { created: 1, sent: 2, executed: 3 };

function furthestStage(a: LeaseStage | undefined, b: LeaseStage): LeaseStage {
  if (!a) return b;
  return LEASE_STAGE_RANK[b] >= LEASE_STAGE_RANK[a] ? b : a;
}

/**
 * Parse a feed event's date to epoch millis. Feed events come back in several
 * shapes: a Date, an ISO string, an epoch number, or a Firestore timestamp
 * (`{_seconds,_nanoseconds}` from the REST API / `{seconds,nanoseconds}` from
 * the admin SDK). Returns null if unparseable.
 */
function toMillis(v: unknown): number | null {
  if (v == null) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.getTime();
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const s =
      typeof o._seconds === 'number' ? o._seconds : typeof o.seconds === 'number' ? o.seconds : null;
    if (s != null) return s * 1000;
    return null;
  }
  const t = new Date(v as string | number).getTime();
  if (!Number.isNaN(t)) return t;

  // Local addition — see the file header.
  const d = convertPseudoDateToRealDate(v as unknown as Date);
  return d && !Number.isNaN(d.getTime()) ? d.getTime() : null;
}

export function fmtDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Scheduled dates are date-only ("YYYY-MM-DD") → UTC midnight. Use UTC getters
  // so a local timezone behind UTC doesn't shift the day backward.
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}

function buildLabel(v: Omit<UnitStatusView, 'label' | 'asOf'>): string {
  const parts: string[] = [v.occupancy === 'occupied' ? 'Occupied' : 'Vacant'];
  if (v.current?.renewed) parts.push('lease renewed');
  else if (v.current?.renewal === 'negotiating') parts.push('renewal negotiating');
  else if (v.current?.renewal === 'terms_agreed') parts.push('renewal terms agreed');
  if (v.current?.moveOut) {
    parts.push(
      `notice given${v.current.moveOut.date ? `, out ${fmtDate(v.current.moveOut.date)}` : ''}`
    );
  }
  if (v.listing === 'listed') parts.push('listed');
  else if (v.listing === 'showing') parts.push('showing');
  if (v.incoming) {
    const nm = v.incoming.tenantName ? ` (${v.incoming.tenantName})` : '';
    if (v.incoming.moveIn?.date) parts.push(`new tenant${nm} move-in ${fmtDate(v.incoming.moveIn.date)}`);
    else if (v.incoming.lease === 'executed') parts.push(`new lease signed${nm}`);
    else if (v.incoming.lease === 'sent') parts.push(`new lease sent${nm}`);
    else if (v.incoming.lease === 'created') parts.push(`new lease drafted${nm}`);
  }
  return parts.join(' · ');
}

/**
 * Fold a unit's feed into its current status view.
 *
 * Consumes both our `state_*` status events AND the native platform lease
 * events (`lease_created`, `lease_sent`, `document.completed`, ...), so the
 * incoming-tenant pipeline (drafted → sent → executed → moved in) is visible
 * without waiting for a single terminal event.
 *
 * Occupancy defaults to "occupied" (the honest baseline for a leased unit with
 * no recorded move events) and only flips on move-in / move-out.
 *
 * NOTE: that "occupied" baseline is a lie for buildings predating these
 * workflows, which have no move events at all. Prefer `resolveUnitStatus()` in
 * UI code — it keeps these overlays but sources occupancy from the lease when no
 * move event has ever been recorded.
 */
export function deriveUnitStatus(
  events: FeedEvent[],
  options: DeriveOptions = {}
): UnitStatusView {
  const processTypeById = options.processTypeById ?? {};

  const timeline = (events ?? [])
    .filter((e) => e && isRecognizedEvent(e.eventType))
    .map((e) => {
      const ms = toMillis(e.eventDate);
      const data = (e.eventData ?? {}) as StatusEventData;
      const procId = e.processId ?? data.processId;
      return {
        type: e.eventType,
        when: ms ?? 0,
        whenIso: ms == null ? null : new Date(ms).toISOString(),
        data,
        procId,
      };
    })
    .sort((a, b) => a.when - b.when);

  let occupancy: 'occupied' | 'vacant' = 'occupied';
  let current: CurrentTenantStatus | null = null;
  let incoming: IncomingTenantStatus | null = null;
  let listing: 'listed' | 'showing' | null = null;
  let asOf: string | null = null;

  // Resolve a native lease event to renewal vs new lease via its process type.
  const isRenewal = (procId: string | undefined, data: StatusEventData): boolean => {
    if (data.leaseType === 'renewal') return true;
    if (data.leaseType === 'new_lease') return false;
    return procId != null && processTypeById[procId] === 'lease_renewal';
  };

  // Advance the incoming (new) tenant's lease pipeline; a new lease supersedes
  // any listing/showing on the unit.
  const advanceIncoming = (stage: LeaseStage, tenantName?: string): void => {
    incoming = {
      ...(incoming ?? {}),
      tenantName: tenantName ?? incoming?.tenantName,
      lease: furthestStage(incoming?.lease, stage),
    };
    listing = null;
  };

  // Mark the current tenant renewed (lease executed); cancels move-out + the
  // in-progress renewal sub-status.
  const markRenewed = (tenantName?: string): void => {
    current = {
      ...(current ?? {}),
      tenantName: tenantName ?? current?.tenantName,
      renewed: true,
      renewal: undefined,
      moveOut: undefined,
    };
    incoming = null;
    listing = null;
  };

  for (const ev of timeline) {
    asOf = ev.whenIso ?? asOf;
    const d = ev.data;
    switch (ev.type) {
      // ── Renewal sub-status (our events) ──────────────────
      case StatusEvent.RenewalNegotiating: {
        const prev: CurrentTenantStatus = current ?? {};
        current = { ...prev, tenantName: d.tenantName ?? prev.tenantName, renewal: 'negotiating' };
        break;
      }
      case StatusEvent.RenewalTermsAgreed: {
        const prev: CurrentTenantStatus = current ?? {};
        current = { ...prev, tenantName: d.tenantName ?? prev.tenantName, renewal: 'terms_agreed' };
        break;
      }

      // ── Move-out ─────────────────────────────────────────
      case StatusEvent.MoveoutScheduled: {
        const prev: CurrentTenantStatus = current ?? {};
        current = { ...prev, tenantName: d.tenantName ?? prev.tenantName, moveOut: { date: d.date } };
        break;
      }
      case StatusEvent.TenantMovedOut:
        occupancy = 'vacant';
        current = null;
        break;

      // ── Listing ──────────────────────────────────────────
      case StatusEvent.ListingPublished:
        listing = 'listed';
        break;
      case StatusEvent.UnitShown:
        listing = 'showing';
        break;

      // ── Lease lifecycle (native events, classified by process type) ──
      case NativeLeaseEvent.LeaseCreated:
        if (isRenewal(ev.procId, d)) {
          /* renewal drafting handled by renewal sub-status */
        } else advanceIncoming('created', d.tenantName);
        break;
      case NativeLeaseEvent.LeaseSent:
      case NativeLeaseEvent.LeaseResent:
        if (!isRenewal(ev.procId, d)) advanceIncoming('sent', d.tenantName);
        break;
      case NativeLeaseEvent.DocumentCompleted:
      case NativeLeaseEvent.LeaseExecuted:
        if (isRenewal(ev.procId, d)) markRenewed(d.tenantName);
        else advanceIncoming('executed', d.tenantName);
        break;

      // ── Lease executed (our event — carries leaseType) ───
      case StatusEvent.LeaseSigned:
        if (d.leaseType === 'renewal') markRenewed(d.tenantName);
        else advanceIncoming('executed', d.tenantName);
        break;

      // ── Move-in / move-in scheduling ─────────────────────
      case StatusEvent.MoveinScheduled: {
        const prev: IncomingTenantStatus = incoming ?? {};
        incoming = { ...prev, tenantName: d.tenantName ?? prev.tenantName, moveIn: { date: d.date } };
        listing = null;
        break;
      }
      case StatusEvent.TenantMovedIn:
        // Incoming tenant becomes the sitting tenant; the cycle resets.
        occupancy = 'occupied';
        current = { tenantName: incoming?.tenantName ?? d.tenantName };
        incoming = null;
        listing = null;
        break;
    }
  }

  const view = { occupancy, current, incoming, listing };
  return { ...view, label: buildLabel(view), asOf };
}

/** Rebuild the one-line label after occupancy has been overridden. */
export function relabel(view: Omit<UnitStatusView, 'label'>): UnitStatusView {
  return { ...view, label: buildLabel(view) };
}
